import { useLazyQuery } from '@apollo/client'
import { Nullable, useUrlSearchParams } from '@dai/common'
import {
  Box,
  Grid2 as Grid,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import { isEmpty, round } from 'lodash'
import { useEffect } from 'react'

import { FeatureMessage } from '@/components/common/UpgradeMessage'
import { useRefresh } from '@/contexts/RefreshContext'
import {
  GET_PRODUCT_DASHBOARD_STATS,
  ProductDashboardOrdering,
  ProductDashboardStatFragment,
  ProductsDashboardFilter,
  ProductsDashboardStatsQuery,
  ProductsDashboardStatsQueryVariables,
  SortDirection,
} from '@/graphql'
import { usePosDataSetupStatus } from '@/hooks/use-pos-data-setup-status'
import { useTableOrder } from '@/hooks/useTableOrder'
import { useTablePageState } from '@/hooks/useTablePageState'
import { formatCurrency, formatNumber } from '@/i18n/tolgee'
import useT from '@/i18n/useT'

import DeliciousTable, { HeadCell } from '../../components/DeliciousTable'

export default function ProductCategoriesTable({
  filters,
  singleStoreId,
}: {
  filters?: ProductsDashboardFilter
  singleStoreId?: Nullable<string>
}) {
  const { t } = useT()
  const {
    handle: { handleNavTo },
  } = useUrlSearchParams()

  const productCategoriesHeaders: HeadCell<ProductDashboardOrdering>[] = [
    {
      id: 'category',
      alignment: 'left',
      label: t('common.department'),
      sortKey: 'category',
    },
    {
      id: 'revenue',
      alignment: 'right',
      label: t('common.revenue'),
      sortKey: 'totalRevenue',
    },
    {
      id: 'margin',
      alignment: 'right',
      label: t('common.margin'),
      sortKey: 'totalEarnings',
    },
    {
      id: 'volume',
      alignment: 'right',
      label: t('common.volume'),
      sortKey: 'volume',
    },
    {
      id: 'vpo',
      alignment: 'right',
      label: t('common.vpo'),
      sortKey: 'vpo',
    },
    // TODO: the backend query needs to be modifies to handle when grouping by category
    // we will need to group by date, category, store then sum...then take an average of that
    // it does not seem worth it right now...
    // { id: 'facings', alignment: 'left', label: 'Facings', sortKey: 'facings' },
    // {
    //   id: 'emptyFacings',
    //   alignment: 'left',
    //   label: t('common.emptyFacings'),
    //   sortKey: 'emptyFacings',
    // },
    // { id: 'epf', alignment: 'left', label: t('common.epf'), sortKey: 'revenuePerFacing' },
    // { id: 'mpf', alignment: 'left', label: t('common.mpf'), sortKey: 'earningsPerFacing' },
  ]

  const { page, rowsPerPage, setPage, setRowsPerPage } = useTablePageState()

  const [
    fetchProductDashboardStatsStores,
    {
      data: productDashboardStatsStores,
      loading: productDashboardStatsStoresLoading,
      error: productDashboardStatsStoresError,
      refetch,
      variables,
    },
  ] = useLazyQuery<
    ProductsDashboardStatsQuery,
    ProductsDashboardStatsQueryVariables
  >(GET_PRODUCT_DASHBOARD_STATS, { notifyOnNetworkStatusChange: true })

  useRefresh(() => {
    if (!isEmpty(variables)) {
      refetch()
    }
  })

  const { orderBy, order, handleRequestSort } =
    useTableOrder<ProductDashboardOrdering>('totalRevenue', SortDirection.DESC)

  useEffect(() => {
    if (filters?.startDate && filters?.endDate) {
      fetchProductDashboardStatsStores({
        variables: {
          filters: {
            startDate: filters.startDate,
            endDate: filters.endDate,
            storeIds: filters.storeIds,
            groupBy: 'category',
            includePreviousPosVolume: false,
          },
          ordering: { [orderBy]: order },
          pagination: { limit: rowsPerPage, offset: rowsPerPage * page },
        },
      })
    }
  }, [filters, page, rowsPerPage, orderBy, order])

  const renderRow = (
    stat: ProductDashboardStatFragment,
    index: number,
    onRowClick: (category: string | null) => void,
  ) => {
    return (
      <TableRow
        hover
        role="checkbox"
        tabIndex={-1}
        key={`${stat.id}-${index}`}
        onClick={() => onRowClick(stat.name || '')}
        sx={{ cursor: 'pointer' }}
      >
        <TableCell>
          <Box ml={3}>{stat.name || stat.id}</Box>
        </TableCell>
        <TableCell align="right">
          <Typography>
            {stat.totalRevenue === null || stat.totalRevenue === undefined
              ? t('storesReportTable.noData')
              : formatCurrency(round(stat.totalRevenue))}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography>
            {stat?.totalEarnings === null || stat?.totalEarnings === undefined
              ? t('storesReportTable.noData')
              : formatCurrency(round(stat.totalEarnings))}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography>
            {stat?.volume === null || stat?.volume === undefined
              ? t('storesReportTable.noData')
              : formatNumber(stat.volume)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography>
            {stat?.vpo === null || stat?.vpo === undefined
              ? t('storesReportTable.noData')
              : formatNumber(stat.vpo)}
          </Typography>
        </TableCell>
        {/* <TableCell align="left">
          <Typography>
            {stat?.facings === null || stat?.facings === undefined
              ? t('storesReportTable.noData')
              : round(stat.facings).toLocaleString()}
          </Typography>
        </TableCell>
        <TableCell align="left">
          <Typography>
            {stat?.emptyFacings === null || stat?.emptyFacings === undefined
              ? t('storesReportTable.noData')
              : round(stat.emptyFacings).toLocaleString()}
          </Typography>
        </TableCell>
        <TableCell align="left">
          <Typography>
            {stat?.revenuePerFacing === null ||
            stat?.revenuePerFacing === undefined
              ? t('storesReportTable.noData')
              : stat.revenuePerFacing.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
          </Typography>
        </TableCell>
        <TableCell align="left">
          <Typography>
            {stat?.earningsPerFacing === null ||
            stat?.earningsPerFacing === undefined
              ? t('storesReportTable.noData')
              : stat.earningsPerFacing.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
          </Typography>
        </TableCell> */}
      </TableRow>
    )
  }

  const { hasDataInBigQuery, numUnlinkedStores, noLinkedStores, loading } =
    usePosDataSetupStatus({ storeIds: filters?.storeIds || undefined })

  return (
    <Grid size={{ xs: 12 }}>
      <DeliciousTable<ProductDashboardStatFragment, ProductDashboardOrdering>
        title={t('categoriesTable.title')}
        data={
          productDashboardStatsStores?.productsDashboardStats?.results || []
        }
        totalCount={
          productDashboardStatsStores?.productsDashboardStats?.totalCount || 0
        }
        headCells={productCategoriesHeaders}
        renderRow={(category, index) =>
          renderRow(category, index, category => {
            handleNavTo(`/products`, {
              replaceParams: category
                ? {
                    categories: [category],
                    ...(singleStoreId ? { storeIds: [singleStoreId] } : {}),
                  }
                : {},
            })
          })
        }
        noDataMessage={t('categoriesTable.noData')}
        orderBy={orderBy}
        order={order === SortDirection.ASC ? 'asc' : 'desc'}
        handleRequestSort={handleRequestSort}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        loading={productDashboardStatsStoresLoading || loading}
        error={productDashboardStatsStoresError?.message}
        BodyComponent={
          (!hasDataInBigQuery || noLinkedStores) && (
            <TableRow>
              <TableCell colSpan={productCategoriesHeaders.length}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  {!hasDataInBigQuery ? (
                    <FeatureMessage
                      sx={{ width: '100%', maxWidth: 400 }}
                      message={t('noSalesData.message')}
                      buttonText={t('noSalesData.button')}
                      onClick={() => {
                        handleNavTo('/admin/sales-data')
                      }}
                    />
                  ) : (
                    <FeatureMessage
                      sx={{ width: '100%', maxWidth: 400 }}
                      message={t('unlinkedStores.message', {
                        numUnlinkedStores,
                      })}
                      buttonText={t('unlinkedStores.button')}
                      onClick={() => {
                        handleNavTo('/admin/sales-data')
                      }}
                    />
                  )}
                </Box>
              </TableCell>
            </TableRow>
          )
        }
      />
    </Grid>
  )
}
